import type { RootState } from 'store';
import type { WaitlistEntry } from 'types/waitlist';

export const selectWaitlistState = (state: RootState) => state.waitlist;

export const selectWaitlistVersion = (state: RootState): number => state.waitlist.version;

export const selectWaitlistUpdatedAt = (state: RootState): number => state.waitlist.updatedAt;

export const selectWaitlistPlayerGuid = (state: RootState): string => state.waitlist.playerGuid;

export const selectWaitlistEntryCount = (state: RootState): number => state.waitlist.entryCount;

export const selectMySpotFor = (gameId: number) =>
  (state: RootState): WaitlistEntry | undefined =>
    state.waitlist.mySpotByGameId[gameId];

export const selectIsQueuedFor = (gameId: number) =>
  (state: RootState): boolean =>
    Boolean(state.waitlist.mySpotByGameId[gameId]);

export const selectWaitlistCountFor = (gameId: number) =>
  (state: RootState): number => {
    const mine = state.waitlist.mySpotByGameId[gameId];
    if (mine) return mine.waitlistCount;
    const bucket = state.waitlist.entriesByGameId[gameId];
    return bucket ? bucket.length : 0;
  };

export const selectActiveOffer = (state: RootState) => state.waitlist.pendingOffer;

export const selectMySpotHasOffer = (gameId: number) =>
  (state: RootState): boolean => {
    const spot = state.waitlist.mySpotByGameId[gameId];
    return Boolean(spot?.yourTurn);
  };

/**
 * Canonical "is there a live seat offer for me here?" check. Mirrors Unity's
 * WaitListController.IsYourTurn exactly: YourTurn && AvailableSeats > 0.
 * See Assets/Scripts/Controllers/WaitListController.cs:45.
 */
export const selectMySpotHasValidOffer = (gameId: number) =>
  (state: RootState): boolean => {
    const spot = state.waitlist.mySpotByGameId[gameId];
    if (!spot) return false;
    return spot.yourTurn && spot.availableSeats > 0;
  };

/**
 * Mirrors Unity WaitListController.IsAutoSeat at
 * Assets/Scripts/Controllers/WaitListController.cs:48 — true when my waitlist
 * entry carries an autoBuyIn amount, so the engine will auto-seat me without
 * any manual confirmation. Used to suppress the CONFIRM_SEAT CTA.
 */
export const selectMySpotIsAutoSeat = (gameId: number) =>
  (state: RootState): boolean => {
    const spot = state.waitlist.mySpotByGameId[gameId];
    return Boolean(spot && spot.autoBuyIn > 0);
  };
